import { useState, useEffect, useCallback, useRef } from 'react';
import { X, RefreshCw, Lock, Flag, ExternalLink, Copy, Check } from 'lucide-react';
import {
  getAllFlags,
  setFlag,
  FLAG_DESCRIPTIONS,
  FEATURE_FLAGS,
  type FeatureFlagKey,
  type FlagInfo,
} from '../../lib/featureFlags';

interface FeatureFlagsPanelProps {
  onClose: () => void;
}

export function FeatureFlagsPanel({ onClose }: FeatureFlagsPanelProps) {
  const [flags, setFlags] = useState<Record<FeatureFlagKey, FlagInfo>>(() => getAllFlags());
  const [hasChanges, setHasChanges] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  // Build URL with current flag state as query params
  const buildUrlWithFlags = useCallback(() => {
    const url = new URL(window.location.href);
    // Clear existing ff_ params
    Array.from(url.searchParams.keys())
      .filter((k) => k.startsWith('ff_'))
      .forEach((k) => url.searchParams.delete(k));
    // Only add flags that are overridden AND differ from default value
    Object.entries(flags).forEach(([key, info]) => {
      const typedKey = key as FeatureFlagKey;
      if (info.source !== 'default' && info.value !== (FEATURE_FLAGS as Record<string, boolean>)[typedKey]) {
        url.searchParams.set(`ff_${key}`, String(info.value));
      }
    });
    return url.toString();
  }, [flags]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Close on click outside
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const handleToggle = (key: FeatureFlagKey) => {
    const currentInfo = flags[key];
    // Can't toggle URL-overridden flags
    if (currentInfo.source === 'url') return;

    const newValue = !currentInfo.value;
    // Always persist the new value to localStorage
    setFlag(key, newValue);

    // Update local state
    setFlags(getAllFlags());
    setHasChanges(true);
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleOpenNewWindow = () => {
    window.open(buildUrlWithFlags(), '_blank', 'noopener,noreferrer');
  };

  const handleCopyUrl = async () => {
    const url = buildUrlWithFlags();
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopied(true);
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }
    copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
  };

  const flagEntries = Object.entries(flags) as [FeatureFlagKey, FlagInfo][];

  return (
    <div
      className="fixed inset-0 z-50"
      onClick={handleBackdropClick}
    >
      <div className="fixed bottom-16 left-4 w-80 bg-surface-900 rounded-lg shadow-2xl border border-surface-700">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-surface-700">
          <div className="flex items-center gap-2 text-text-primary">
            <Flag className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-medium">Feature Flags</span>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-surface-700 rounded text-text-secondary"
            title="Close (Esc)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Warning banner */}
        <div className="px-3 py-2 bg-amber-900/30 border-b border-amber-700/50 text-amber-200 text-xs flex items-center gap-2">
          <RefreshCw className="w-3 h-3" />
          <span>Changes take effect after refresh</span>
        </div>

        {/* Flags list */}
        <div className="max-h-64 overflow-y-auto">
          {flagEntries.map(([key, info]) => (
            <div
              key={key}
              className="px-3 py-3 border-b border-surface-800 last:border-b-0"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono text-text-primary truncate">
                      {key}
                    </span>
                    <span className="text-xs text-text-tertiary">
                      ({info.source})
                    </span>
                  </div>
                  <p className="text-xs text-text-secondary mt-0.5">
                    {FLAG_DESCRIPTIONS[key]}
                  </p>
                </div>

                {/* Toggle or Lock */}
                {info.source === 'url' ? (
                  <div className="flex items-center gap-1 text-text-tertiary" title="URL override - cannot toggle">
                    <Lock className="w-3 h-3" />
                    <span className="text-xs uppercase font-medium">
                      {info.value ? 'on' : 'off'}
                    </span>
                  </div>
                ) : (
                  <button
                    onClick={() => handleToggle(key)}
                    className={`
                      relative w-10 h-5 rounded-full transition-colors
                      ${info.value ? 'bg-green-600' : 'bg-surface-600'}
                    `}
                    title={`Toggle ${key}`}
                  >
                    <span
                      className={`
                        absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform
                        ${info.value ? 'left-5' : 'left-0.5'}
                      `}
                    />
                  </button>
                )}
              </div>

              {/* URL override indicator */}
              {info.source === 'url' && (
                <div className="mt-2 flex items-center gap-1 text-xs text-amber-400">
                  <Lock className="w-3 h-3" />
                  <span>URL override active</span>
                </div>
              )}
            </div>
          ))}

          {flagEntries.length === 0 && (
            <div className="px-3 py-4 text-center text-text-tertiary text-sm">
              No feature flags defined
            </div>
          )}
        </div>

        {/* Footer with action buttons */}
        <div className="px-3 py-2 border-t border-surface-700 space-y-2">
          <button
            onClick={handleRefresh}
            className={`
              w-full py-1.5 px-3 rounded text-sm font-medium flex items-center justify-center gap-2
              ${hasChanges
                ? 'bg-amber-600 hover:bg-amber-500 text-white'
                : 'bg-surface-700 hover:bg-surface-600 text-text-secondary'
              }
            `}
          >
            <RefreshCw className="w-4 h-4" />
            {hasChanges ? 'Refresh to Apply' : 'Refresh Page'}
          </button>
          <div className="flex gap-2">
            <button
              onClick={handleOpenNewWindow}
              className="flex-1 py-1.5 px-3 rounded text-sm font-medium flex items-center justify-center gap-2 bg-surface-700 hover:bg-surface-600 text-text-secondary"
              title="Open in new window with current flag settings"
            >
              <ExternalLink className="w-4 h-4" />
              New Window
            </button>
            <button
              onClick={handleCopyUrl}
              className="py-1.5 px-3 rounded text-sm font-medium flex items-center justify-center gap-2 bg-surface-700 hover:bg-surface-600 text-text-secondary"
              title={copied ? 'Copied!' : 'Copy URL with flags'}
              aria-label={copied ? 'URL copied' : 'Copy URL with flags'}
            >
              {copied ? (
                <Check className="w-4 h-4 text-success-400" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
